import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useSearchParams, useParams } from 'react-router-dom';
import { Container, Card, Button, Form, Row, Col, Alert, Spinner } from 'react-bootstrap';
import { Edit, Package, Save } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { IdBadge, IdBadgeType } from '../../../components/ui/badge/IdBadge';
import { StatusBadge } from '../../../components/ui/badge/StatusBadge';
import { ImageGallery } from '../../../components/common/ImageGallery';
import { SaveConfirmationModal } from './components/SaveConfirmationModal';
import { SourcingBackupLinksEditor } from './components/SourcingBackupLinksEditor';
import { OfficialStoreUrlsEditor } from './components/OfficialStoreUrlsEditor';
import { FeaturedImageEditor } from './components/FeaturedImageEditor';
import { VariationImagesEditor } from './components/VariationImagesEditor';
import { ProductBarcodeField } from './components/ProductBarcodeField';
import {
  CreditBoostEditor,
  getCreditBoostSelectionKey,
  mergeCreditBoostReadback,
  type CreditBoostReadback,
} from './components/CreditBoostEditor';
import { ProductFilters } from './components/ProductFilters';
import { ProductTableHeader } from './components/ProductTableHeader';
import { CpBatchData } from './components/CpBatchData';
import { AdminProductCard } from '../../sourcing/components/admin-product-card/AdminProductCard';
import { buildChangedAttributesPayload } from './utils/changeDetection';
import {
  buildVariationImagesPayload,
  getVariationDisplayImages,
  resetVariationImagesToParent,
  withEditedVariationImages,
} from './utils/variationImages';
import { normalizeProductSourcing, parseOfficialStoreUrls } from '../../../utils/sourcingLinks';
import { isInvalidEditedProductBarcode } from '@/utils/productBarcode';
import { decodeHtmlEntities } from '@/utils/stringUtils';
import type { SkinSeoulAdminProduct, CpProductStatus, ProductAttributes } from '../../../models/ProductTypes';
import { PublicationStatus, StockStatus, SkinSeoulProductType } from '../../../models/ProductTypes';
import {
  skinSeoulProductsClient,
  type CategoryTerm,
  type BrandTerm,
} from '../../../infrastructure/SkinSeoulProductsClient';

// The editor reads past the 7-day product cache, so fields such as hasOwnImages are never stale.
const EDITOR_LOAD = { current: true };

interface CategoryNode extends CategoryTerm {
  children: CategoryNode[];
  path: string[];
}

const SSEditorPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sku: skuParam } = useParams<{ sku?: string }>();
  const [selectedProduct, setSelectedProduct] = useState<SkinSeoulAdminProduct | null>(null);
  const [originalProduct, setOriginalProduct] = useState<SkinSeoulAdminProduct | null>(null);
  const [showSaveConfirmation, setShowSaveConfirmation] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isBoostSaving, setIsBoostSaving] = useState(false);
  // SKUs whose variation image upload is in flight: Save waits for the edited product's, or the payload would miss
  // the uploaded image. An upload left running on another product does not hold this one.
  const [uploadingImageSkus, setUploadingImageSkus] = useState<ReadonlySet<string>>(new Set());
  const setVariationImageUploading = (sku: string, isUploading: boolean) =>
    setUploadingImageSkus((current) => {
      const next = new Set(current);
      if (isUploading) {
        next.add(sku);
      } else {
        next.delete(sku);
      }
      return next;
    });
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [isLoadingProduct, setIsLoadingProduct] = useState(false);
  const [showVariations, setShowVariations] = useState(false);
  const processedSkuRef = useRef<string | null>(null);

  // Search enabled flag
  const [isSearchEnabled, setIsSearchEnabled] = useState(false);

  // Local filter state (temporary filters before search)
  const [localFilters, setLocalFilters] = useState({
    productType: 'All' as SkinSeoulProductType | 'All',
    postStatus: 'All' as PublicationStatus | 'All',
    stockStatus: 'All' as StockStatus | 'All',
    wave: 'all',
    brand: '',
    category: '',
    sku: '',
    name: '',
    cpProductStatus: 'All' as CpProductStatus | 'All',
    cpLastScrapedAt: '',
  });

  // Applied filters (actual filters used in query)
  const [appliedFilters, setAppliedFilters] = useState({
    productType: 'All' as SkinSeoulProductType | 'All',
    postStatus: 'All' as PublicationStatus | 'All',
    stockStatus: 'All' as StockStatus | 'All',
    wave: 'all',
    brand: '',
    category: '',
    sku: '',
    name: '',
    cpProductStatus: 'All' as CpProductStatus | 'All',
    cpLastScrapedAt: '',
  });

  // Pagination state
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 100,
  });

  // Categories and Brands state
  const [categoryPaths, setCategoryPaths] = useState<
    Array<{
      display: string;
      fullPath: string;
      thumbnailImageUrl?: string;
      level: number;
      isRoot: boolean;
      termId: number;
    }>
  >([]);
  const [brands, setBrands] = useState<BrandTerm[]>([]);
  const categoriesLoadedRef = useRef(false);

  // Resizable layout state
  const [leftWidth, setLeftWidth] = useState(50); // 50% for left panel
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Fetch SkinSeoul products with filters
  const { data: productsData, isLoading } = useQuery({
    queryKey: ['ss-editor-products', appliedFilters, pagination.page, pagination.limit],
    queryFn: async () => {
      if (appliedFilters.sku.trim()) {
        const products = await skinSeoulProductsClient.searchProductsBySku(
          appliedFilters.sku.trim(),
          appliedFilters.postStatus === 'All' ? undefined : appliedFilters.postStatus,
        );

        return {
          products,
          total: products.length,
          page: 1,
          limit: products.length,
        };
      }

      return skinSeoulProductsClient.getAllProducts(
        pagination.page,
        pagination.limit,
        appliedFilters.postStatus === 'All' ? undefined : appliedFilters.postStatus,
        appliedFilters.productType === 'All' ? undefined : appliedFilters.productType,
        appliedFilters.stockStatus === 'All' ? undefined : appliedFilters.stockStatus,
        appliedFilters.wave === 'all' ? undefined : appliedFilters.wave,
        appliedFilters.brand || undefined, // brandIds (termId)
        appliedFilters.category || undefined, // categoryIds (termId)
        appliedFilters.cpProductStatus === 'All' ? undefined : appliedFilters.cpProductStatus,
        appliedFilters.cpLastScrapedAt || undefined,
        appliedFilters.name || undefined,
      );
    },
    enabled: isSearchEnabled,
    staleTime: 5 * 60 * 1000,
  });

  const products = productsData?.products || [];
  const totalProducts = productsData?.total || 0;

  // Update local filters only (without triggering search)
  const updateLocalFilters = (newFilters: Partial<typeof localFilters>) => {
    setLocalFilters((prev) => ({ ...prev, ...newFilters }));
  };

  // Execute search with current local filters
  const executeSearch = () => {
    // Find termIds for brand and category
    const brandTermId = localFilters.brand
      ? brands.find((b) => b.name === localFilters.brand)?.termId.toString()
      : undefined;
    const categoryTermId = localFilters.category
      ? categoryPaths.find((c) => c.fullPath === localFilters.category)?.termId.toString()
      : undefined;

    setAppliedFilters({
      productType: localFilters.productType,
      postStatus: localFilters.postStatus,
      stockStatus: localFilters.stockStatus,
      wave: localFilters.wave,
      brand: brandTermId || '',
      category: categoryTermId || '',
      sku: localFilters.sku,
      name: localFilters.name,
      cpProductStatus: localFilters.cpProductStatus,
      cpLastScrapedAt: localFilters.cpLastScrapedAt,
    });
    setIsSearchEnabled(true);
    setPagination({ page: 1, limit: pagination.limit });

    // Update URL with all filter parameters
    const params: Record<string, string> = {};
    if (localFilters.sku) {
      params['sku'] = localFilters.sku;
    }
    if (localFilters.name) {
      params['name'] = localFilters.name;
    }
    if (localFilters.productType && localFilters.productType !== 'All') {
      params['productType'] = localFilters.productType;
    }
    if (localFilters.postStatus && localFilters.postStatus !== 'All') {
      params['postStatus'] = localFilters.postStatus;
    }
    if (localFilters.stockStatus && localFilters.stockStatus !== 'All') {
      params['stockStatus'] = localFilters.stockStatus;
    }
    if (localFilters.wave && localFilters.wave !== 'all') {
      params['wave'] = localFilters.wave;
    }
    if (localFilters.brand) {
      params['brand'] = localFilters.brand;
    }
    if (localFilters.category) {
      params['category'] = localFilters.category;
    }
    if (localFilters.cpProductStatus && localFilters.cpProductStatus !== 'All') {
      params['cpProductStatus'] = localFilters.cpProductStatus;
    }
    if (localFilters.cpLastScrapedAt) {
      params['cpLastScrapedAt'] = localFilters.cpLastScrapedAt;
    }
    setSearchParams(params);
  };

  // Handle page change
  const handlePageChange = (page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  };

  // Handle page size change
  const handlePageSizeChange = (limit: number) => {
    setPagination({ page: 1, limit });
  };

  // Fetch categories on mount
  useEffect(() => {
    // Prevent multiple fetches using ref
    if (categoriesLoadedRef.current) {
      return;
    }
    categoriesLoadedRef.current = true;

    const buildCategoryTree = (categories: CategoryTerm[]): CategoryNode[] => {
      const categoryMap = new Map<number, CategoryNode>();
      const rootCategories: CategoryNode[] = [];

      // Create nodes
      categories.forEach((cat) => {
        categoryMap.set(cat.termId, { ...cat, children: [], path: [] });
      });

      // Build tree and paths
      categories.forEach((cat) => {
        const node = categoryMap.get(cat.termId)!;
        if (cat.parentTermId) {
          const parent = categoryMap.get(cat.parentTermId);
          if (parent) {
            parent.children.push(node);
            node.path = [...parent.path, parent.name];
          }
        } else {
          rootCategories.push(node);
        }
      });

      return rootCategories;
    };

    const extractCategoryPaths = (
      nodes: CategoryNode[],
      level = 0,
    ): Array<{
      display: string;
      fullPath: string;
      thumbnailImageUrl?: string;
      level: number;
      isRoot: boolean;
      termId: number;
    }> => {
      const paths: Array<{
        display: string;
        fullPath: string;
        thumbnailImageUrl?: string;
        level: number;
        isRoot: boolean;
        termId: number;
      }> = [];

      nodes.forEach((node) => {
        const displayName = node.name;
        const fullPath = [...node.path, node.name].join(' › ');

        paths.push({
          display: displayName,
          fullPath,
          ...(node.thumbnailImageUrl ? { thumbnailImageUrl: node.thumbnailImageUrl } : {}),
          level,
          isRoot: level === 0,
          termId: node.termId,
        });

        if (node.children.length > 0) {
          paths.push(...extractCategoryPaths(node.children, level + 1));
        }
      });

      return paths;
    };

    const fetchCategoriesAndBrands = async () => {
      try {
        console.info('🔄 Fetching categories and brands...');
        const [fetchedCategories, fetchedBrands] = await Promise.all([
          skinSeoulProductsClient.getAllCategories(),
          skinSeoulProductsClient.getAllBrands(),
        ]);
        const tree = buildCategoryTree(fetchedCategories);
        const paths = extractCategoryPaths(tree);
        setCategoryPaths(paths);
        setBrands(fetchedBrands);
        console.info('✅ Loaded category tree:', tree.length, 'root categories,', paths.length, 'total paths');
        console.info('✅ Loaded brands:', fetchedBrands.length);
      } catch (error) {
        console.error('❌ Failed to fetch categories and brands:', error);
      }
    };

    fetchCategoriesAndBrands();
  }, []);

  // No client-side filtering needed - all filters are handled by API
  const filteredProducts = products;

  // Resizable splitter handlers
  const handleMouseDown = (e: React.MouseEvent) => {
    setIsResizing(true);
    e.preventDefault();
  };

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isResizing || !containerRef.current) {
        return;
      }

      const container = containerRef.current;
      const containerRect = container.getBoundingClientRect();
      const mouseX = e.clientX - containerRect.left;
      const containerWidth = containerRect.width;
      const mousePercentage = (mouseX / containerWidth) * 100;

      const newLeftWidth = Math.min(Math.max(mousePercentage, 30), 70); // 30% to 70%
      setLeftWidth(newLeftWidth);
    },
    [isResizing],
  );

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  // Add event listeners for mouse move and up
  React.useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  // Function to fetch fresh product data
  const fetchFreshProductData = async (product: SkinSeoulAdminProduct) => {
    setIsLoadingProduct(true);
    try {
      // For variable products, we need to get the main product with all variations
      if (product.type === SkinSeoulProductType.Variable) {
        // Search by SKU to get the main product with variations
        const freshProducts = await skinSeoulProductsClient.searchProductsBySku(product.sku, undefined, EDITOR_LOAD);
        const mainProduct = freshProducts.find((p) => p.productId === product.productId);
        if (mainProduct) {
          setSelectedProduct(mainProduct);
          setOriginalProduct(JSON.parse(JSON.stringify(mainProduct)));
        }
      } else if (product.type === SkinSeoulProductType.Variation) {
        // For variations, search by SKU to get the parent variable product with all variations
        const freshProducts = await skinSeoulProductsClient.searchProductsBySku(product.sku, undefined, EDITOR_LOAD);
        // Find the parent variable product (not the variation itself)
        const parentProduct = freshProducts.find((p) => p.type === SkinSeoulProductType.Variable);
        if (parentProduct) {
          setSelectedProduct(parentProduct);
          setOriginalProduct(JSON.parse(JSON.stringify(parentProduct)));
        }
      } else {
        // For simple products, use the same API as variable products - search by SKU
        const freshProducts = await skinSeoulProductsClient.searchProductsBySku(product.sku, undefined, EDITOR_LOAD);
        const freshProduct = freshProducts.find((p) => p.productId === product.productId);
        if (freshProduct) {
          setSelectedProduct(freshProduct);
          setOriginalProduct(JSON.parse(JSON.stringify(freshProduct)));
        }
      }
    } catch (error) {
      console.error('Failed to fetch fresh product data:', error);
      // Fallback to using the existing product data
      setSelectedProduct(product);
      setOriginalProduct(JSON.parse(JSON.stringify(product)));
    } finally {
      setIsLoadingProduct(false);
    }
  };

  const applyCreditBoostReadback = (selectionKey: string, values: CreditBoostReadback[]) => {
    const mergeCurrent = (current: SkinSeoulAdminProduct | null) =>
      current && getCreditBoostSelectionKey(current) === selectionKey
        ? mergeCreditBoostReadback(current, values)
        : current;
    setSelectedProduct(mergeCurrent);
    setOriginalProduct(mergeCurrent);
  };

  // Handle filter parameters from URL (both route param and search params)
  useEffect(() => {
    const skuFromRoute = skuParam;
    const skuFromSearch = searchParams.get('sku');
    const targetSku = skuFromRoute || skuFromSearch;

    // Read all filter parameters from URL
    const productTypeParam = searchParams.get('productType') as SkinSeoulProductType | null;
    const postStatusParam = searchParams.get('postStatus') as PublicationStatus | null;
    const stockStatusParam = searchParams.get('stockStatus') as StockStatus | null;
    const waveParam = searchParams.get('wave');
    const brandParam = searchParams.get('brand');
    const categoryParam = searchParams.get('category');
    const nameParam = searchParams.get('name');
    const cpProductStatusParam = searchParams.get('cpProductStatus') as CpProductStatus | null;
    const cpLastScrapedAtParam = searchParams.get('cpLastScrapedAt');

    // Check if any URL parameters exist
    const hasUrlParams =
      targetSku ||
      productTypeParam ||
      postStatusParam ||
      stockStatusParam ||
      waveParam ||
      brandParam ||
      categoryParam ||
      nameParam ||
      cpProductStatusParam ||
      cpLastScrapedAtParam;

    if (hasUrlParams && processedSkuRef.current !== targetSku) {
      processedSkuRef.current = targetSku || null;

      // Update local filters with URL parameters
      const newFilters: Partial<typeof localFilters> = {};
      if (targetSku) {
        newFilters.sku = targetSku;
      }
      if (nameParam) {
        newFilters.name = nameParam;
      }
      if (productTypeParam) {
        newFilters.productType = productTypeParam;
      }
      if (postStatusParam) {
        newFilters.postStatus = postStatusParam;
      }
      if (stockStatusParam) {
        newFilters.stockStatus = stockStatusParam;
      }
      if (waveParam) {
        newFilters.wave = waveParam;
      }
      if (brandParam) {
        newFilters.brand = brandParam;
      }
      if (categoryParam) {
        newFilters.category = categoryParam;
      }
      if (cpProductStatusParam) {
        newFilters.cpProductStatus = cpProductStatusParam;
      }
      if (cpLastScrapedAtParam) {
        newFilters.cpLastScrapedAt = cpLastScrapedAtParam;
      }

      setLocalFilters((prev) => ({ ...prev, ...newFilters }));

      // Find termIds for brand and category
      const brandTermId = brandParam ? brands.find((b) => b.name === brandParam)?.termId.toString() : undefined;
      const categoryTermId = categoryParam
        ? categoryPaths.find((c) => c.fullPath === categoryParam)?.termId.toString()
        : undefined;

      // Update applied filters with URL parameters
      const newAppliedFilters: Partial<typeof appliedFilters> = {};
      if (targetSku) {
        newAppliedFilters.sku = targetSku;
      }
      if (nameParam) {
        newAppliedFilters.name = nameParam;
      }
      if (productTypeParam) {
        newAppliedFilters.productType = productTypeParam;
      }
      if (postStatusParam) {
        newAppliedFilters.postStatus = postStatusParam;
      }
      if (stockStatusParam) {
        newAppliedFilters.stockStatus = stockStatusParam;
      }
      if (waveParam) {
        newAppliedFilters.wave = waveParam;
      }
      if (brandTermId) {
        newAppliedFilters.brand = brandTermId;
      }
      if (categoryTermId) {
        newAppliedFilters.category = categoryTermId;
      }
      if (cpProductStatusParam) {
        newAppliedFilters.cpProductStatus = cpProductStatusParam;
      }
      if (cpLastScrapedAtParam) {
        newAppliedFilters.cpLastScrapedAt = cpLastScrapedAtParam;
      }

      setAppliedFilters((prev) => ({ ...prev, ...newAppliedFilters }));
      setIsSearchEnabled(true);
    } else if (!hasUrlParams && processedSkuRef.current !== null) {
      processedSkuRef.current = null;
      setLocalFilters((prev) => ({ ...prev, sku: '' }));
    }
  }, [skuParam, searchParams, brands, categoryPaths]);

  const renderProductEditor = () => {
    if (!selectedProduct) {
      return (
        <div className="text-center text-muted py-5">
          <Edit size={48} className="mb-1 opacity-50" />
          <h5>Select a Product to Edit</h5>
          <p>Choose a SkinSeoul product from the table to edit its details.</p>
        </div>
      );
    }

    if (isLoadingProduct) {
      return (
        <div className="text-center text-muted py-5">
          <Spinner animation="border" role="status" className="mb-3">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <h5>Loading Product Data...</h5>
          <p>Fetching fresh product information from the server.</p>
        </div>
      );
    }

    const isVariable = selectedProduct.type === SkinSeoulProductType.Variable;
    const productsToEdit =
      isVariable && selectedProduct.variations ? [selectedProduct, ...selectedProduct.variations] : [selectedProduct];
    const originalProductsToEdit =
      isVariable && originalProduct?.variations ? [originalProduct, ...originalProduct.variations] : [originalProduct];
    const hasInvalidBarcode = productsToEdit.some((product, index) =>
      isInvalidEditedProductBarcode(product.attributes?.barcode, originalProductsToEdit[index]?.attributes?.barcode),
    );
    const isUploadingVariationImage = productsToEdit.some((product) => uploadingImageSkus.has(product.sku));

    // Helper function to update product fields
    const updateProductField = (index: number, field: string, value: any) => {
      if (index === 0) {
        // Update main product
        setSelectedProduct({ ...selectedProduct, [field]: value });
      } else {
        // Update variation
        const variations = [...(selectedProduct.variations || [])];
        variations[index - 1] = { ...variations[index - 1], [field]: value } as SkinSeoulAdminProduct;
        setSelectedProduct({ ...selectedProduct, variations });
      }
    };

    const updateProductAttributes = (
      index: number,
      product: SkinSeoulAdminProduct,
      partialAttributes: Partial<ProductAttributes>,
    ) => {
      const updatedProduct = {
        ...product,
        attributes: { ...product.attributes, ...partialAttributes },
      } as SkinSeoulAdminProduct;

      if (index === 0) {
        setSelectedProduct(updatedProduct);
      } else {
        const variations = [...(selectedProduct.variations || [])];
        variations[index - 1] = updatedProduct;
        setSelectedProduct({ ...selectedProduct, variations });
      }
    };

    // The publish already landed server-side; this only moves the row's own copy forward so the new
    // image shows without a refetch. The gallery entries are untouched, matching what the API did.
    //
    // It must merge into the CURRENT state, not the product captured when the row rendered: this is
    // an editor holding unsaved changes, and a publish can finish after further edits. Spreading the
    // captured snapshot would silently discard them. The identity check drops a response that
    // arrives after the user has moved to a different product.
    const replaceFeaturedImage = (publishedProduct: SkinSeoulAdminProduct, wpFeaturedImageUrl: string) => {
      setSelectedProduct((currentProduct) => {
        if (!currentProduct || currentProduct.productId !== publishedProduct.productId) {
          return currentProduct;
        }

        const existingImageUrls = currentProduct.productImageUrls || [];
        return {
          ...currentProduct,
          productImageUrls: [wpFeaturedImageUrl, ...existingImageUrls.slice(1)],
        } as SkinSeoulAdminProduct;
      });
    };

    // Variation image edits stay local until Save. Merges into the CURRENT state, since an upload can
    // finish after further edits; the SKU check drops a result for a product the user has left.
    const updateVariationImages = (
      index: number,
      sku: string,
      transform: (variation: SkinSeoulAdminProduct, parentImageUrls: string[]) => SkinSeoulAdminProduct,
    ) => {
      setSelectedProduct((currentProduct) => {
        const variation = currentProduct?.variations?.[index - 1];
        if (!currentProduct || !variation || variation.sku !== sku) {
          return currentProduct;
        }
        const variations = [...(currentProduct.variations || [])];
        variations[index - 1] = transform(variation, currentProduct.productImageUrls || []);
        return { ...currentProduct, variations };
      });
    };
    const parentImageUrls = productsToEdit[0]?.productImageUrls;

    return (
      <div>
        {/* Success/Error Message */}
        {saveMessage && (
          <Alert
            variant={saveMessage.type === 'success' ? 'success' : 'danger'}
            className="mb-3"
            dismissible
            onClose={() => setSaveMessage(null)}
          >
            {saveMessage.text}
          </Alert>
        )}

        {/* Invalid Product URL Warning */}
        {(!selectedProduct.productLink?.includes('skin-seoul.com/product/') ||
          selectedProduct.productLink?.endsWith('product//') ||
          selectedProduct.productLink?.endsWith('product/') ||
          selectedProduct.productLink?.endsWith('product')) && (
          <Alert variant="warning" className="mb-3">
            <strong>⚠️ Not found name slug:</strong> Product must have a valid URL with product name slug to publish.
            You can publish via WP Admin or CM Workflow step 3 or step 8.
          </Alert>
        )}

        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="mb-1">
              {isVariable
                ? `Editing Variable Product & ${selectedProduct.variations?.length || 0} Variations`
                : 'Editing Product'}
            </h6>
            <small className="text-muted">Changes apply to all listed products below</small>
          </div>
          <div className="d-flex gap-2">
            <Button
              variant="primary"
              size="sm"
              onClick={() => setShowSaveConfirmation(true)}
              disabled={isSaving || isBoostSaving || hasInvalidBarcode || isUploadingVariationImage}
            >
              {isSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-1" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="me-1" />
                  Save Changes
                </>
              )}
            </Button>
          </div>
        </div>

        <CreditBoostEditor
          product={selectedProduct}
          isProductSaving={isSaving || showSaveConfirmation}
          onSavingChange={setIsBoostSaving}
          onConfirmed={applyCreditBoostReadback}
        />

        {/* Save Confirmation Modal */}
        <SaveConfirmationModal
          show={showSaveConfirmation}
          onHide={() => setShowSaveConfirmation(false)}
          onConfirm={async () => {
            if (isBoostSaving || hasInvalidBarcode || isUploadingVariationImage) {
              return;
            }
            setIsSaving(true);
            setSaveMessage(null);
            setShowSaveConfirmation(false);

            try {
              // Build hierarchical payload structure
              const mainProduct = productsToEdit[0];
              const variations = productsToEdit.slice(1);

              if (!mainProduct) {
                throw new Error('No main product found');
              }

              // Helper function to build product payload with only changed values
              const buildProductPayload = (
                rawProduct: SkinSeoulAdminProduct,
                originalVersion: SkinSeoulAdminProduct | null,
                isVariation = false,
              ) => {
                // On save, clean backups (Coupang/Naver) + official store urls, and revert preferred when its source is gone.
                let product = rawProduct;
                const rawAttributes = rawProduct.attributes;
                if (
                  rawAttributes &&
                  (rawAttributes.backup_sources !== undefined ||
                    rawAttributes.official_store_urls !== undefined ||
                    rawAttributes.preferred_source !== undefined)
                ) {
                  const normalizedSourcing = normalizeProductSourcing(
                    rawAttributes.backup_sources,
                    rawAttributes.official_store_urls,
                    rawAttributes.preferred_source,
                    rawAttributes.cp_url,
                  );
                  product = {
                    ...rawProduct,
                    attributes: {
                      ...rawAttributes,
                      backup_sources: normalizedSourcing.backupSources,
                      official_store_urls: normalizedSourcing.officialStoreUrls,
                      preferred_source: normalizedSourcing.preferredSource,
                    },
                  };
                }

                if (!originalVersion) {
                  // If no original version, send all values
                  const basePayload: any = {
                    sku: product.sku,
                    ...(product.name && { name: product.name }),
                    ...(product.postStatus && { status: product.postStatus }),
                    ...(product.status && { stockStatus: product.status }),
                    ...(product.attributes && { attributes: product.attributes }),
                    ...(product.originalPrice !== undefined && { originalPrice: product.originalPrice }),
                    ...(product.salePrice !== undefined && { salePrice: product.salePrice }),
                  };

                  // Add fields only for main product (not variations)
                  if (!isVariation) {
                    if (product.category) {
                      basePayload.categories = product.category;
                    }
                    if (product.brand) {
                      basePayload.brand = product.brand;
                    }
                  }

                  return basePayload;
                }

                // Build payload with only changed values
                const payload: any = { sku: product.sku };

                // Check for changes in basic fields
                if (product.name !== originalVersion.name) {
                  payload.name = product.name;
                }
                if (product.postStatus !== originalVersion.postStatus) {
                  payload.status = product.postStatus;
                }
                if (product.status !== originalVersion.status) {
                  payload.stockStatus = product.status;
                }

                // Check for price changes
                if (product.originalPrice !== originalVersion.originalPrice) {
                  payload.originalPrice = product.originalPrice;
                }
                // Only include salePrice if it was present in the original API response
                if (originalVersion.salePrice !== undefined && product.salePrice !== originalVersion.salePrice) {
                  payload.salePrice = product.salePrice;
                }

                // Add fields only for main product (not variations)
                if (!isVariation) {
                  if (JSON.stringify(product.category) !== JSON.stringify(originalVersion.category)) {
                    payload.categories = product.category;
                  }
                  if (product.brand !== originalVersion.brand) {
                    payload.brand = product.brand;
                  }
                }

                // Check for changes in attributes using shared utility
                const changedAttributes = buildChangedAttributesPayload(product, originalVersion);

                // Only include attributes if there are changes
                if (Object.keys(changedAttributes).length > 0) {
                  payload.attributes = changedAttributes;
                }

                return payload;
              };

              // Build main product payload
              const mainProductPayload = buildProductPayload(mainProduct, originalProduct || null, false);

              // Build variations payload. `images` is only sent when this variation's images were edited.
              const variationsPayload = variations.map((variation, index) => {
                const originalVariation = originalProduct?.variations?.[index] || null;
                return {
                  ...buildProductPayload(variation, originalVariation, true),
                  ...buildVariationImagesPayload(variation, originalVariation),
                };
              });

              // Add variations to main product if there are any
              if (variationsPayload.length > 0) {
                mainProductPayload.variations = variationsPayload;
              }

              const productsPayload = [mainProductPayload];

              const result = await skinSeoulProductsClient.updateProducts(productsPayload);

              if (result.success) {
                setSaveMessage({ type: 'success', text: result.message || 'Products updated successfully!' });
                setTimeout(() => setSaveMessage(null), 5000);

                // Update local products with fresh data
                if (selectedProduct) {
                  try {
                    let freshProduct: SkinSeoulAdminProduct | null = null;

                    // For variable products and variations, search by SKU to get updated data
                    if (
                      selectedProduct.type === SkinSeoulProductType.Variable ||
                      selectedProduct.type === SkinSeoulProductType.Variation
                    ) {
                      const freshProducts = await skinSeoulProductsClient.searchProductsBySku(
                        selectedProduct.sku,
                        undefined,
                        EDITOR_LOAD,
                      );
                      // For variations, find the parent variable product
                      if (selectedProduct.type === SkinSeoulProductType.Variation) {
                        freshProduct = freshProducts.find((p) => p.type === SkinSeoulProductType.Variable) || null;
                      } else {
                        freshProduct = freshProducts.find((p) => p.productId === selectedProduct.productId) || null;
                      }
                    } else {
                      // For simple products, use the same API as variable products - search by SKU
                      const freshProducts = await skinSeoulProductsClient.searchProductsBySku(
                        selectedProduct.sku,
                        undefined,
                        EDITOR_LOAD,
                      );
                      freshProduct = freshProducts.find((p) => p.productId === selectedProduct.productId) || null;
                    }

                    if (freshProduct) {
                      // Update selected product
                      setSelectedProduct(freshProduct);
                      setOriginalProduct(JSON.parse(JSON.stringify(freshProduct)));
                    }
                  } catch (error) {
                    console.error('Failed to fetch updated product data:', error);
                    // Don't show error to user since save was successful
                  }
                }
              } else {
                setSaveMessage({ type: 'error', text: result.message || 'Failed to update products' });
              }
            } catch (error) {
              console.error('Error saving products:', error);
              setSaveMessage({
                type: 'error',
                text: error instanceof Error ? error.message : 'Unknown error occurred',
              });
            } finally {
              setIsSaving(false);
            }
          }}
          originalProduct={originalProduct}
          productsToEdit={productsToEdit}
        />

        {/* Publication Status - Only for Variable/Simple products */}
        {selectedProduct.type !== SkinSeoulProductType.Variation && (
          <Card className="shadow-sm mb-2" style={{ backgroundColor: '#f8f9fa' }}>
            <Card.Body className="p-2">
              <Form.Label style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                Publication Status
              </Form.Label>
              <div className="d-flex gap-2">
                <Button
                  variant={selectedProduct.postStatus === PublicationStatus.PUBLISH ? 'dark' : 'outline-dark'}
                  size="sm"
                  style={{ flex: 1 }}
                  onClick={() => {
                    setSelectedProduct({ ...selectedProduct, postStatus: PublicationStatus.PUBLISH });
                  }}
                  disabled={
                    !selectedProduct.productLink?.includes('skin-seoul.com/product/') ||
                    selectedProduct.productLink?.endsWith('product//') ||
                    selectedProduct.productLink?.endsWith('product/') ||
                    selectedProduct.productLink?.endsWith('product')
                  }
                >
                  Publish
                </Button>
                <Button
                  variant={selectedProduct.postStatus === PublicationStatus.DRAFT ? 'secondary' : 'outline-secondary'}
                  size="sm"
                  style={{ flex: 1 }}
                  onClick={() => {
                    setSelectedProduct({ ...selectedProduct, postStatus: PublicationStatus.DRAFT });
                  }}
                >
                  Draft
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}

        {/* Category and Brand - At Top */}
        <Card className="mb-2 shadow-sm" style={{ backgroundColor: '#f8f9fa' }}>
          <Card.Body className="p-2">
            <Row className="g-3">
              <Col md={6}>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Category
                </Form.Label>
                <Form.Select
                  size="sm"
                  defaultValue={selectedProduct.category?.join(' › ') || ''}
                  onChange={(e) => {
                    setSelectedProduct({
                      ...selectedProduct,
                      category: e.target.value ? e.target.value.split(' › ') : [],
                    });
                  }}
                >
                  <option value="">Select Category</option>
                  {categoryPaths.map((pathObj) => (
                    <option key={pathObj.fullPath} value={pathObj.fullPath}>
                      {pathObj.display}
                    </option>
                  ))}
                </Form.Select>
              </Col>
              <Col md={6}>
                <Form.Label style={{ fontSize: '0.85rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                  Brand
                </Form.Label>
                <Form.Select
                  size="sm"
                  // Brand options use decoded names, while the product brand keeps WordPress '&amp;'
                  defaultValue={decodeHtmlEntities(selectedProduct.brand || '')}
                  onChange={(e) => {
                    setSelectedProduct({ ...selectedProduct, brand: e.target.value });
                  }}
                >
                  <option value="">Select Brand</option>
                  {brands
                    .slice()
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((brand) => (
                      <option key={brand.termId} value={brand.name}>
                        {brand.name}
                      </option>
                    ))}
                </Form.Select>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Products to Edit */}
        {productsToEdit.map((product, index) => (
          <Card
            key={product.productId}
            className="mb-3 shadow-sm"
            style={{
              backgroundColor: '#fff',
              marginLeft: index > 0 ? '2rem' : '0',
            }}
          >
            <Card.Body className="p-2" style={{ overflow: 'visible' }}>
              <div className="d-flex" style={{ position: 'relative', minHeight: '120px' }}>
                {/* Hierarchy indicator for variations */}
                {index > 0 && (
                  <div className="me-2 flex-shrink-0 d-flex align-items-start">
                    <small
                      className="text-muted me-1"
                      style={{ fontSize: '0.7rem', fontWeight: 'bold', marginTop: '2px' }}
                    >
                      ↳
                    </small>
                  </div>
                )}

                {/* Number */}
                <div className="me-2 flex-shrink-0 d-flex align-items-start">
                  <span style={{ fontSize: '0.8rem', fontWeight: 'bold', marginTop: '2px', color: '#6c757d' }}>
                    {index === 0 ? '1' : `1.${index}`}
                  </span>
                </div>

                {/* Image and Gallery. An inherited variation shows the parent's images. */}
                <div className="me-3 flex-shrink-0">
                  <ImageGallery
                    productImages={
                      index === 0 ? product.productImageUrls : getVariationDisplayImages(product, parentImageUrls)
                    }
                    learnMoreImages={index === 0 ? product.learnMoreUrl : undefined}
                    title={`${product.name} - Image Gallery`}
                    mainImageSize={{ width: 80, height: 80 }}
                    thumbnailSize={{ width: 25, height: 25 }}
                  />
                  {index > 0 && product.hasOwnImages === false && (
                    <div className="text-muted text-center" style={{ fontSize: '0.6rem', marginTop: '2px' }}>
                      Inherited
                    </div>
                  )}
                </div>

                {/* Product Details */}
                <div
                  className="flex-grow-1 position-relative"
                  style={{ minWidth: 0, paddingRight: '130px', paddingBottom: '20px' }}
                >
                  {/* Badges */}
                  <div className="d-flex gap-2 flex-wrap mb-2">
                    <StatusBadge type="productType" value={product.type || 'simple'} size="md" />
                    <StatusBadge type="publication" value={product.postStatus || 'draft'} size="md" />
                    <StatusBadge type="stock" value={product.status || 'outofstock'} size="md" />
                  </div>

                  {/* Copiable Badges */}
                  <div className="mb-2 d-flex gap-1 flex-wrap">
                    <IdBadge type={IdBadgeType.SKU} value={product.sku} size="sm" />
                    <IdBadge type={IdBadgeType.SPI} value={product.productId || 0} size="sm" />
                    {product.attributes?.gp_product_id && (
                      <IdBadge
                        type={IdBadgeType.GPI}
                        value={product.attributes.gp_product_id}
                        size="sm"
                        truncate
                        title="Click to copy GP Product ID"
                      />
                    )}
                    {product.attributes?.cp_parent_code && (
                      <IdBadge
                        type={IdBadgeType.CPPI}
                        value={product.attributes.cp_parent_code}
                        size="sm"
                        title="Click to copy CP Parent Code"
                      />
                    )}
                  </div>

                  {/* Product Name - Editable (Only for Variable/Simple) */}
                  {product.type !== SkinSeoulProductType.Variation && (
                    <>
                      <div className="mb-2">
                        <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>Product Name</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={selectedProduct.name}
                          onChange={(e) => setSelectedProduct({ ...selectedProduct, name: e.target.value })}
                          placeholder="Product name"
                          style={{ fontWeight: '600' }}
                        />
                      </div>
                      <div className="mb-2">
                        <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>SS Name Kor</Form.Label>
                        <Form.Control
                          size="sm"
                          type="text"
                          value={product.attributes?.ss_name_kor || ''}
                          onChange={(e) => {
                            const updatedProduct = {
                              ...product,
                              attributes: { ...product.attributes, ss_name_kor: e.target.value },
                            };
                            if (index === 0) {
                              setSelectedProduct(updatedProduct);
                            } else {
                              const updatedVariations = [...(selectedProduct.variations || [])];
                              updatedVariations[index - 1] = updatedProduct;
                              setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                            }
                          }}
                          placeholder="e.g., 스킨서울 클렌징 폼"
                        />
                      </div>
                    </>
                  )}

                  {/* Stock Status - For Variable, Simple and Variation */}
                  {(product.type === SkinSeoulProductType.Variable ||
                    product.type === SkinSeoulProductType.Simple ||
                    product.type === SkinSeoulProductType.Variation) && (
                    <div className="mb-2">
                      <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600', marginBottom: '0.5rem' }}>
                        Stock Status
                      </Form.Label>
                      <div className="d-flex gap-2">
                        <Button
                          variant={product.status === StockStatus.IN_STOCK ? 'success' : 'outline-success'}
                          size="sm"
                          style={{ flex: 1 }}
                          onClick={() => {
                            const updatedProduct = { ...product, status: StockStatus.IN_STOCK };
                            if (index === 0) {
                              setSelectedProduct(updatedProduct);
                            } else {
                              const updatedVariations = [...(selectedProduct.variations || [])];
                              updatedVariations[index - 1] = updatedProduct;
                              setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                            }
                          }}
                        >
                          In Stock
                        </Button>
                        <Button
                          variant={product.status === StockStatus.OUT_OF_STOCK ? 'danger' : 'outline-danger'}
                          size="sm"
                          style={{ flex: 1 }}
                          onClick={() => {
                            const updatedProduct = { ...product, status: StockStatus.OUT_OF_STOCK };
                            if (index === 0) {
                              setSelectedProduct(updatedProduct);
                            } else {
                              const updatedVariations = [...(selectedProduct.variations || [])];
                              updatedVariations[index - 1] = updatedProduct;
                              setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                            }
                          }}
                        >
                          Out of Stock
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Stock Status Lock - stops the hourly auto stock sync from overriding a manually set status */}
                  {(product.type === SkinSeoulProductType.Variable ||
                    product.type === SkinSeoulProductType.Simple ||
                    product.type === SkinSeoulProductType.Variation) && (
                    <div className="mb-2">
                      <Form.Check
                        type="switch"
                        id={`ss-stock-lock-${product.productId ?? index}`}
                        checked={product.attributes?.ss_stock_locked === 'true'}
                        label={
                          <span style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                            Lock stock status{' '}
                            <span style={{ fontWeight: '400', color: '#6c757d' }}>
                              (stops the hourly auto stock sync from changing the status)
                            </span>
                          </span>
                        }
                        onChange={(e) => {
                          const updatedProduct = {
                            ...product,
                            attributes: { ...product.attributes, ss_stock_locked: e.target.checked ? 'true' : '' },
                          };
                          if (index === 0) {
                            setSelectedProduct(updatedProduct);
                          } else {
                            const updatedVariations = [...(selectedProduct.variations || [])];
                            updatedVariations[index - 1] = updatedProduct;
                            setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                          }
                        }}
                      />
                    </div>
                  )}

                  {/* Editable Fields */}
                  <div className="row g-2 mt-2">
                    {/* Size, Color, Pack - Only editable for Variable/Simple */}
                    {(product.type === SkinSeoulProductType.Variable ||
                      product.type === SkinSeoulProductType.Simple) && (
                      <>
                        <div className="col-md-3">
                          <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>Size</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={product.size || ''}
                            onChange={(e) => {
                              const newSize = e.target.value;
                              const updatedProduct = { ...product, size: newSize };
                              if (index === 0) {
                                // Update main product and all variations
                                const updatedVariations = (selectedProduct.variations || []).map((variation) => ({
                                  ...variation,
                                  size: newSize,
                                }));
                                setSelectedProduct({ ...updatedProduct, variations: updatedVariations });
                              } else {
                                const updatedVariations = [...(selectedProduct.variations || [])];
                                updatedVariations[index - 1] = updatedProduct;
                                setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                              }
                            }}
                            placeholder=""
                          />
                        </div>
                        <div className="col-md-3">
                          <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>Color</Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={product.color || ''}
                            onChange={(e) => {
                              const newColor = e.target.value;
                              const updatedProduct = { ...product, color: newColor };
                              if (index === 0) {
                                // Update main product and all variations
                                const updatedVariations = (selectedProduct.variations || []).map((variation) => ({
                                  ...variation,
                                  color: newColor,
                                }));
                                setSelectedProduct({ ...updatedProduct, variations: updatedVariations });
                              } else {
                                const updatedVariations = [...(selectedProduct.variations || [])];
                                updatedVariations[index - 1] = updatedProduct;
                                setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                              }
                            }}
                            placeholder=""
                          />
                        </div>
                        <div className="col-md-6">
                          <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                            Pack <span className="text-danger">*</span>
                          </Form.Label>
                          <Form.Control
                            size="sm"
                            type="text"
                            value={product.pack || ''}
                            onChange={(e) => {
                              const updatedProduct = { ...product, pack: e.target.value };
                              if (index === 0) {
                                setSelectedProduct(updatedProduct);
                              } else {
                                const updatedVariations = [...(selectedProduct.variations || [])];
                                updatedVariations[index - 1] = updatedProduct;
                                setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                              }
                            }}
                            placeholder=""
                            required
                          />
                        </div>
                      </>
                    )}

                    {/* Size, Color, Pack for Variations - Size & Color are read-only */}
                    {product.type === SkinSeoulProductType.Variation &&
                      (() => {
                        const parentProduct = productsToEdit[0];
                        if (!parentProduct) {
                          return null;
                        }

                        // Get pack options from parent variable product
                        const parentPack = parentProduct.pack || parentProduct.attributes?.attribute_pack || '';

                        // Handle different possible formats for pack data
                        let packOptions: string[] = [];

                        if (parentPack) {
                          // Try different separators
                          if (parentPack.includes('|')) {
                            packOptions = parentPack
                              .split('|')
                              .map((p) => p.trim())
                              .filter((p) => p.length > 0);
                          } else if (parentPack.includes(',')) {
                            packOptions = parentPack
                              .split(',')
                              .map((p) => p.trim())
                              .filter((p) => p.length > 0);
                          } else if (parentPack.includes(';')) {
                            packOptions = parentPack
                              .split(';')
                              .map((p) => p.trim())
                              .filter((p) => p.length > 0);
                          } else {
                            // Single pack option
                            packOptions = [parentPack.trim()].filter((p) => p.length > 0);
                          }
                        }

                        return (
                          <>
                            <div className="col-md-3">
                              <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>Size</Form.Label>
                              <Form.Control
                                size="sm"
                                type="text"
                                value={product.size || ''}
                                readOnly
                                disabled
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                            <div className="col-md-3">
                              <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>Color</Form.Label>
                              <Form.Control
                                size="sm"
                                type="text"
                                value={product.color || ''}
                                readOnly
                                disabled
                                style={{ backgroundColor: '#f8f9fa' }}
                              />
                            </div>
                            {packOptions.length > 0 ? (
                              <div className="col-md-6">
                                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                  Pack <span className="text-danger">*</span>
                                </Form.Label>
                                <Form.Select
                                  size="sm"
                                  value={product.pack || ''}
                                  onChange={(e) => updateProductField(index, 'pack', e.target.value)}
                                  required
                                >
                                  <option value="">Select Pack</option>
                                  {packOptions.map((option) => (
                                    <option key={option} value={option}>
                                      {option}
                                    </option>
                                  ))}
                                </Form.Select>
                              </div>
                            ) : (
                              <div className="col-md-6">
                                <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                                  Pack <span className="text-danger">*</span>
                                </Form.Label>
                                <div className="text-muted" style={{ fontSize: '0.8rem' }}>
                                  No pack options available from parent product
                                </div>
                              </div>
                            )}
                          </>
                        );
                      })()}

                    {/* Barcode - Only for Variable/Simple */}
                    {(product.type === SkinSeoulProductType.Variable ||
                      product.type === SkinSeoulProductType.Simple) && (
                      <ProductBarcodeField
                        barcode={product.attributes?.barcode}
                        originalBarcode={originalProductsToEdit[index]?.attributes?.barcode}
                        onBarcodeChange={(barcode) => {
                          const updatedProduct = {
                            ...product,
                            attributes: { ...product.attributes, barcode },
                          } as SkinSeoulAdminProduct;
                          if (index === 0) {
                            setSelectedProduct(updatedProduct);
                          } else {
                            const updatedVariations = [...(selectedProduct.variations || [])];
                            updatedVariations[index - 1] = updatedProduct;
                            setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                          }
                        }}
                      />
                    )}

                    {/* CP URL */}
                    <div className="col-md-12">
                      <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>CP URL</Form.Label>
                      <Form.Control
                        size="sm"
                        type="url"
                        value={product.attributes?.cp_url || ''}
                        onChange={(e) => {
                          const updatedProduct = {
                            ...product,
                            attributes: { ...product.attributes, cp_url: e.target.value },
                          } as SkinSeoulAdminProduct;
                          if (index === 0) {
                            setSelectedProduct(updatedProduct);
                          } else {
                            const updatedVariations = [...(selectedProduct.variations || [])];
                            updatedVariations[index - 1] = updatedProduct;
                            setSelectedProduct({ ...selectedProduct, variations: updatedVariations });
                          }
                        }}
                        placeholder="https://..."
                      />
                    </div>

                    {/* Backup sourcing links (scraped) + preferred source */}
                    <SourcingBackupLinksEditor
                      backupSourcesJson={product.attributes?.backup_sources}
                      preferredSource={product.attributes?.preferred_source}
                      mainCoupangUrl={product.attributes?.cp_url}
                      hasOfficialStoreUrl={parseOfficialStoreUrls(product.attributes?.official_store_urls).some(
                        (officialStoreUrl) => officialStoreUrl.trim() !== '',
                      )}
                      onBackupSourcesChange={(serializedBackupSources) =>
                        updateProductAttributes(index, product, { backup_sources: serializedBackupSources })
                      }
                      onPreferredSourceChange={(preferredSourceValue) =>
                        updateProductAttributes(index, product, { preferred_source: preferredSourceValue })
                      }
                    />

                    {/* Official store urls (bookkeeping only) */}
                    <OfficialStoreUrlsEditor
                      officialStoreUrlsJson={product.attributes?.official_store_urls}
                      onOfficialStoreUrlsChange={(serializedOfficialStoreUrls) =>
                        updateProductAttributes(index, product, { official_store_urls: serializedOfficialStoreUrls })
                      }
                    />

                    {/* Featured image: WordPress downloads the URL itself, so nothing is uploaded from
                                        the browser. Main product only - the endpoint rejects a variation SKU. */}
                    {index === 0 && (
                      <FeaturedImageEditor
                        sku={product.sku}
                        onPublished={(wpFeaturedImageUrl) => replaceFeaturedImage(product, wpFeaturedImageUrl)}
                      />
                    )}

                    {/* Variation images: saved with the rest via variations[].images (full replacement). */}
                    {index > 0 && (
                      <VariationImagesEditor
                        sku={product.sku}
                        imageUrls={getVariationDisplayImages(product, parentImageUrls)}
                        hasOwnImages={product.hasOwnImages}
                        onChange={(imageUrls) =>
                          updateVariationImages(index, product.sku, (variation, currentParentImageUrls) =>
                            withEditedVariationImages(variation, imageUrls, currentParentImageUrls),
                          )
                        }
                        onReset={() =>
                          updateVariationImages(index, product.sku, (variation, currentParentImageUrls) =>
                            resetVariationImagesToParent(variation, currentParentImageUrls),
                          )
                        }
                        onUploadingChange={(isUploading) => setVariationImageUploading(product.sku, isUploading)}
                      />
                    )}

                    {/* CP Product Data & Regular Price - Only for Simple and Variation */}
                    {(product.type === SkinSeoulProductType.Simple ||
                      product.type === SkinSeoulProductType.Variation) && (
                      <>
                        {/* Display CP Product Data if available */}
                        <div className="col-md-12 mt-2">
                          <CpBatchData
                            cpListPrice={product.attributes?.cp_list_price ?? undefined}
                            cpProductStatus={product.attributes?.cp_product_status ?? undefined}
                            cpShippingDate={product.attributes?.cp_shipping_date ?? undefined}
                            cpLastScrapedAt={product.attributes?.cp_last_scraped_at ?? undefined}
                            cpLastError={product.attributes?.cp_last_error ?? undefined}
                          />
                        </div>

                        {/* Regular Price (SGD) - Display Only */}
                        <div className="col-md-6 mt-2">
                          <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                            Regular Price (SGD)
                            {product.attributes?.cp_list_price && (
                              <span className="text-muted ms-2" style={{ fontSize: '0.7rem' }}>
                                • CP: ₩{product.attributes.cp_list_price}
                              </span>
                            )}
                          </Form.Label>
                          <div
                            style={{
                              padding: '0.375rem 0.75rem',
                              fontSize: '0.875rem',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.25rem',
                              minHeight: '31px',
                            }}
                          >
                            {product.originalPrice !== undefined && product.originalPrice !== null
                              ? `SGD ${product.originalPrice.toFixed(2)}`
                              : 'No Price'}
                          </div>
                        </div>

                        {/* Sale Price (SGD) - Display Only */}
                        <div className="col-md-6 mt-2">
                          <Form.Label style={{ fontSize: '0.75rem', fontWeight: '600' }}>
                            Sale Price (SGD)
                            {product.attributes?.cp_discount_price && (
                              <span className="text-muted ms-2" style={{ fontSize: '0.7rem' }}>
                                • CP: ₩{product.attributes.cp_discount_price}
                              </span>
                            )}
                          </Form.Label>
                          <div
                            style={{
                              padding: '0.375rem 0.75rem',
                              fontSize: '0.875rem',
                              backgroundColor: '#f8f9fa',
                              border: '1px solid #dee2e6',
                              borderRadius: '0.25rem',
                              minHeight: '31px',
                            }}
                          >
                            {product.salePrice !== undefined && product.salePrice !== null
                              ? `SGD ${product.salePrice.toFixed(2)}`
                              : 'No Sale Price'}
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Container fluid className="py-2">
      {/* Header */}
      <div className="mb-3 d-flex align-items-center">
        <Edit size={20} className="me-2" />
        <h4 className="mb-0">SS Editor</h4>
      </div>

      {/* Resizable Layout */}
      <div
        ref={containerRef}
        style={{
          display: 'flex',
          height: 'calc(100vh - 100px)',
          overflow: 'hidden',
        }}
      >
        {/* Left Panel - Product Table */}
        <div
          style={{
            width: `${leftWidth}%`,
            minWidth: '400px',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Card className="h-100">
            <Card.Header style={{ backgroundColor: '#000', color: '#fff', position: 'relative', zIndex: 10 }}>
              <ProductTableHeader
                filteredCount={filteredProducts.length}
                totalProducts={totalProducts}
                pagination={pagination}
                showVariations={showVariations}
                setShowVariations={setShowVariations}
                handlePageChange={handlePageChange}
                handlePageSizeChange={handlePageSizeChange}
              />

              <ProductFilters
                filters={{
                  productType: localFilters.productType,
                  postStatus: localFilters.postStatus,
                  stockStatus: localFilters.stockStatus,
                  wave: localFilters.wave,
                  brand: localFilters.brand,
                  category: localFilters.category,
                  sku: localFilters.sku,
                  name: localFilters.name,
                  cpProductStatus: localFilters.cpProductStatus,
                  cpLastScrapedAt: localFilters.cpLastScrapedAt,
                }}
                brands={brands}
                categoryPaths={categoryPaths}
                updateFilters={updateLocalFilters}
                executeSearch={executeSearch}
                isLoading={isLoading}
              />
            </Card.Header>

            <Card.Body
              className="p-1"
              style={{ height: 'calc(100vh - 280px)', overflowY: 'auto', overflowX: 'hidden', position: 'relative' }}
            >
              {/* Loading Overlay */}
              {isLoading && (
                <div
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(255, 255, 255, 0.9)',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1000,
                    borderRadius: '4px',
                  }}
                >
                  <Spinner
                    animation="border"
                    role="status"
                    style={{ width: '3rem', height: '3rem', marginBottom: '1rem' }}
                  >
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <h5 className="mb-1">Loading Products...</h5>
                  <p className="text-muted mb-0">Please wait while we fetch the data</p>
                </div>
              )}

              <div className="p-1">
                {filteredProducts.length === 0 && !isLoading ? (
                  <div className="text-center py-5">
                    <Package size={48} className="text-muted mb-3" />
                    <h5>No Products Found</h5>
                    <p className="text-muted">Try adjusting your filters</p>
                  </div>
                ) : (
                  filteredProducts
                    .sort((a, b) => a.sku.localeCompare(b.sku))
                    .map((product, index) => (
                      <React.Fragment key={product.productId}>
                        <AdminProductCard
                          product={product}
                          index={index}
                          isSelected={selectedProduct?.productId === product.productId}
                          onSelect={fetchFreshProductData}
                        />

                        {/* Variations Hidden Indicator */}
                        {!showVariations &&
                          product.type === SkinSeoulProductType.Variable &&
                          product.variations &&
                          product.variations.length > 0 && (
                            <div
                              className="ms-4 mb-2 p-2"
                              style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}
                            >
                              <small className="text-muted">
                                <Package size={12} className="me-1" />
                                {product.variations.length} variation{product.variations.length !== 1 ? 's' : ''} hidden
                                <span
                                  className="ms-2 text-primary"
                                  style={{ cursor: 'pointer' }}
                                  onClick={() => setShowVariations(true)}
                                >
                                  Show
                                </span>
                              </small>
                            </div>
                          )}

                        {/* Variations */}
                        {showVariations &&
                          product.type === SkinSeoulProductType.Variable &&
                          product.variations &&
                          product.variations.length > 0 &&
                          product.variations
                            .sort((a, b) => a.sku.localeCompare(b.sku))
                            .map((variation, variationIndex) => (
                              <AdminProductCard
                                key={`${product.productId}-var-${variation.productId || variationIndex}`}
                                product={variation}
                                index={index}
                                isSelected={selectedProduct?.productId === variation.productId}
                                onSelect={() => {
                                  setSelectedProduct(product);
                                  setOriginalProduct(JSON.parse(JSON.stringify(product)));
                                }}
                                isVariation={true}
                                parentIndex={index}
                                variationIndex={variationIndex}
                                showImage={true}
                                showQuickLinks={false}
                              />
                            ))}
                      </React.Fragment>
                    ))
                )}
              </div>
            </Card.Body>

            {/* Pagination Footer */}
            {totalProducts > pagination.limit && (
              <Card.Footer
                style={{
                  backgroundColor: '#1a1a1a',
                  borderTop: '1px solid #333',
                  padding: '8px 16px',
                }}
              >
                <div className="d-flex justify-content-between align-items-center">
                  <small style={{ color: '#ccc' }}>
                    Showing {(pagination.page - 1) * pagination.limit + 1} to{' '}
                    {Math.min(pagination.page * pagination.limit, totalProducts)} of {totalProducts} products
                  </small>
                  <div className="d-flex align-items-center gap-2">
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page - 1)}
                      disabled={pagination.page === 1}
                      style={{ borderColor: '#555', color: '#ccc', padding: '4px 12px' }}
                    >
                      ← Previous
                    </Button>
                    <small style={{ color: '#ccc', minWidth: '80px', textAlign: 'center' }}>
                      Page {pagination.page} of {Math.ceil(totalProducts / pagination.limit)}
                    </small>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => handlePageChange(pagination.page + 1)}
                      disabled={pagination.page >= Math.ceil(totalProducts / pagination.limit)}
                      style={{ borderColor: '#555', color: '#ccc', padding: '4px 12px' }}
                    >
                      Next →
                    </Button>
                  </div>
                </div>
              </Card.Footer>
            )}
          </Card>
        </div>

        {/* Resizable Splitter */}
        <div
          onMouseDown={handleMouseDown}
          style={{
            width: '8px',
            backgroundColor: isResizing ? '#007bff' : '#dee2e6',
            cursor: 'col-resize',
            position: 'relative',
            flexShrink: 0,
            borderLeft: '1px solid #ccc',
            borderRight: '1px solid #ccc',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            transition: isResizing ? 'none' : 'background-color 0.2s',
          }}
        >
          <div
            style={{
              width: '2px',
              height: '40px',
              backgroundColor: '#6c757d',
              borderRadius: '1px',
            }}
          />
        </div>

        {/* Right Panel - Product Editor */}
        <div style={{ width: `${100 - leftWidth}%`, minWidth: '400px', display: 'flex', flexDirection: 'column' }}>
          <Card className="h-100">
            <Card.Header style={{ backgroundColor: '#000', color: '#fff', padding: '12px 16px' }}>
              <h6 className="mb-0">Product Editor</h6>
            </Card.Header>
            <Card.Body style={{ overflowY: 'auto', padding: '16px' }}>{renderProductEditor()}</Card.Body>
          </Card>
        </div>
      </div>
    </Container>
  );
};

export default SSEditorPage;
